import {z} from "zod/v4-mini";
import type {TaskRunner} from "../node/utils.js";

// Zod schema and browser-side generator remain the same
export const zEventStreamTask = z.object({
  type: z.literal("event-stream"),
  taskId: z.string(),
  url: z.string(),
  init: z.any(),
  status: z.enum(["initial", "pending", "streaming", "fulfilled", "rejected"]),
  messages: z.array(z.string()),
  result: z.any(),
  done: z.boolean(),
});
export type EventStreamTask = z.output<typeof zEventStreamTask>;

export async function* doEventStream(input: EventStreamTask): AsyncGenerator<{changed: boolean; output: EventStreamTask}, void, EventStreamTask> {
  const output = {...input};
  let resolveNextEvent: (value: {data?: string; done?: boolean; error?: Error}) => void;
  let eventPromise = new Promise<{data?: string; done?: boolean; error?: Error}>((res) => (resolveNextEvent = res));

  // This part runs in the browser, so EventSource is available.
  const eventSource = new EventSource(input.url, input.init);

  eventSource.onopen = () => {
    output.status = "streaming";
  };
  eventSource.onmessage = (event) => {
    const currentResolver = resolveNextEvent;
    eventPromise = new Promise((res) => (resolveNextEvent = res));
    currentResolver({data: event.data});
  };
  eventSource.onerror = () => {
    const currentResolver = resolveNextEvent;
    eventSource.close();
    currentResolver({done: true, error: new Error("EventSource connection error.")});
  };

  // Wait for the connection to be established
  await Promise.race([eventPromise, new Promise((r) => setTimeout(r, 5000))]); // 5s timeout

  if (output.status !== "streaming") {
    output.status = "rejected";
    output.done = true;
    output.result = "Failed to open EventSource connection.";
    yield {changed: true, output};
    return;
  }

  // Send the "streaming" status back
  yield {changed: true, output};

  while (true) {
    const event = await eventPromise;
    if (event.error || event.done) {
      output.status = event.error ? "rejected" : "fulfilled";
      output.result = event.error || null;
      output.done = true;
      yield {changed: true, output};
      break;
    }
    if (event.data) {
      if (event.data === "[DONE]") {
        eventSource.close();
        output.status = "fulfilled";
        output.done = true;
        yield {changed: true, output};
        break;
      }
      output.messages.push(event.data);
      yield {changed: true, output};
    }
  }
}

export const createRunEventStreamInBrowser = (runner: TaskRunner) => {
  return async function* (dir: string, url: string, init?: EventSourceInit): AsyncGenerator<string> {
    const taskId = crypto.randomUUID();
    const initialTask: EventStreamTask = {
      type: "event-stream",
      taskId,
      url,
      init,
      status: "initial",
      messages: [],
      result: null,
      done: false,
    };

    const duplex = await runner<EventStreamTask>({dir, initialTask});
    let yieldedCount = 0;
    let finalTask: EventStreamTask | undefined;

    // We'll use a queue and resolver pattern to turn the event-driven onData into a pull-based async generator.
    const queue: EventStreamTask[] = [];
    let resolveNext: ((task: EventStreamTask | null) => void) | null = null;

    const offData = duplex.onData.on((task) => {
      if (resolveNext) {
        resolveNext(task);
        resolveNext = null;
      } else {
        queue.push(task);
      }
    });

    const offClose = duplex.onClose.once(() => {
      if (resolveNext) {
        resolveNext(null); // Signal end of stream
        resolveNext = null;
      }
    });

    try {
      while (true) {
        let task: EventStreamTask | null;
        if (queue.length > 0) {
          task = queue.shift()!;
        } else {
          task = await new Promise((resolve) => {
            resolveNext = resolve;
          });
        }

        if (task === null) {
          // Stream closed
          break;
        }

        finalTask = task;

        if (yieldedCount === 0 && task.status === "streaming") {
          // Stream confirmed open, continue.
        } else if (task.status !== "streaming" && !task.done) {
          throw task.result || new Error(`Event stream failed. Status: ${task.status}`);
        }

        for (let i = yieldedCount; i < task.messages.length; i++) {
          yield task.messages[i];
        }
        yieldedCount = task.messages.length;

        if (task.done) {
          break;
        }
      }
    } finally {
      offData();
      offClose.off();
      await duplex.close();
    }

    if (finalTask?.status === "rejected") {
      throw finalTask.result || new Error("Event stream ended with a rejection.");
    }
  };
};
